import React, { useState, useEffect } from 'react';
import { Loader2, Tag } from 'lucide-react';
import { apiClient } from '../services/api';
import { useInstance } from '../hooks/useInstance';
import { buildTree, flattenTree } from '../utils/treeUtils';
import { PracticePhrases } from './PracticePhrases';

export interface LeafCard {
  id: number;
  name: string;
}

interface CategoryPracticePhrasesSessionProps {
  categoryId: number;
  categoryName: string;
  onClose: () => void;
  /** Precomputed leaf cards from tree - avoids API call when getCardsHierarchy fails for members */
  initialLeafCards?: LeafCard[];
}

// Fisher-Yates shuffle algorithm
const shuffle = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const CategoryPracticePhrasesSession: React.FC<CategoryPracticePhrasesSessionProps> = ({
  categoryId,
  categoryName,
  onClose,
  initialLeafCards,
}) => {
  const { selectedInstanceId } = useInstance();
  const [categoryCardIds, setCategoryCardIds] = useState<number[]>([]);
  const [cardNames, setCardNames] = useState<Record<number, string>>({});
  const [, setCycleQueue] = useState<number[]>([]);
  const [currentCardId, setCurrentCardId] = useState<number | null>(null);
  const [, setCycleNumber] = useState(1);
  const [, setTotalCards] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialLeafCards && initialLeafCards.length > 0) {
      const ids = initialLeafCards.map((c) => c.id);
      const names: Record<number, string> = {};
      initialLeafCards.forEach((c) => {
        names[c.id] = c.name;
      });
      setCategoryCardIds(ids);
      setCardNames(names);
      setTotalCards(ids.length);
      const shuffled = shuffle(ids);
      setCycleQueue(shuffled.slice(1));
      setCurrentCardId(shuffled[0]);
      setIsLoading(false);
      return;
    }
    if (selectedInstanceId !== null) {
      loadCategoryCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, selectedInstanceId, initialLeafCards]);

  const loadCategoryCards = async () => {
    if (selectedInstanceId === null) return;
    try {
      setIsLoading(true);
      setError(null);

      const cards = await apiClient.getCardsHierarchy(categoryId, selectedInstanceId);
      const treeItems = buildTree(cards);
      const allItems = flattenTree(treeItems);
      const leafCards = allItems.filter((item) => !item.is_folder);

      if (leafCards.length === 0) {
        setError('empty');
        setIsLoading(false);
        return;
      }

      const ids = leafCards.map((item) => item.id);
      const names: Record<number, string> = {};
      leafCards.forEach((item) => {
        names[item.id] = item.name;
      });

      setCategoryCardIds(ids);
      setCardNames(names);
      setTotalCards(ids.length);

      const shuffled = shuffle(ids);
      setCycleQueue(shuffled.slice(1));
      setCurrentCardId(shuffled[0]);
    } catch (err: any) {
      console.error('Error loading category cards:', err);
      setError(err.response?.data?.detail || 'Failed to load category cards');
    } finally {
      setIsLoading(false);
    }
  };

  const goToNextCard = () => {
    setCycleQueue((prevQueue) => {
      const lastCardId = currentCardId;

      if (prevQueue.length > 0) {
        const nextCardId = prevQueue[0];
        setCurrentCardId(nextCardId);
        return prevQueue.slice(1);
      } else {
        setCycleNumber((prev) => prev + 1);
        let shuffled = shuffle(categoryCardIds);

        if (categoryCardIds.length > 1 && lastCardId !== null) {
          if (shuffled[0] === lastCardId) {
            const swapIndex = Math.floor(Math.random() * (shuffled.length - 1)) + 1;
            [shuffled[0], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[0]];
          }
        }

        const nextCardId = shuffled[0];
        setCurrentCardId(nextCardId);
        return shuffled.slice(1);
      }
    });
  };

  if (error === 'empty') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Tag className="h-16 w-16 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No cards in this category yet</p>
              <p className="text-sm text-center mb-6">
                Add cards to this category to practice phrases
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && error !== 'empty') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || currentCardId === null) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <span className="ml-2 text-gray-600">Loading practice session...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PracticePhrases
      key={currentCardId}
      cardId={currentCardId}
      cardName={cardNames[currentCardId] ?? ''}
      onClose={onClose}
      onNext={goToNextCard}
    />
  );
};
